import Phaser from 'phaser';
import { t } from '@i18n/index';
import {
  PALETTE,
  TEXT_COLOUR,
  titleStyle,
  readoutStyle,
  labelStyle,
  FONT,
  px,
  motionMs,
  getReducedMotion,
} from '../theme';
import { createButton } from '../ui/button';
import { layoutOf, makeTappable, watchResize, MIN_HIT } from '../ui/layout';
import { onTap } from '../ui/tap';
import { addBackdrop, MENU_BACKDROP, plateKey, TIER_PLATE } from '../assets';
import { audio } from '../audio/engine';
import { DRONE_ROOT } from '../audio/motifs';
import { PROLOGUE_SOULS, type PrologueChoice } from '@core/campaign/campaign';

/**
 * Der Prolog — die erste Minute des Spiels.
 *
 * Frueher begann ein neues Spiel mit dem Charaktereditor, einer Personalakte
 * und einer stillen Karte. Die eigentliche Frage des Spiels — rette ich
 * Menschen, oder hole ich die Wahrheit vom Grund? — stellte sich erst nach
 * einer halben Stunde. Im Browser entscheiden Spieler in den ersten zwei
 * Minuten, ob sie bleiben.
 *
 * Jetzt beginnt es mitten in der Nacht mit einem Funkspruch, und die
 * Kernentscheidung faellt, bevor man irgendetwas eingestellt hat. Die Wahl
 * wird in die Kampagne uebernommen (`Campaign.applyPrologue`), sie ist also
 * kein Vorspann, sondern der erste Einsatz.
 *
 * Drei Phasen: der Funkspruch, Zeile fuer Zeile; die Wahl zwischen zwei
 * Karten; die Folge und das Motto. Tippen beschleunigt den Funkspruch,
 * „Ueberspringen“ fuehrt direkt zum Editor.
 */

type Phase = 'call' | 'choice' | 'outcome';

/** Zeilen des Funkspruchs mit Zeitpunkt (Sekunden) und Art. */
const CALL: readonly { key: string; at: number; kind: 'stamp' | 'radio' | 'cut' | 'narration' }[] = [
  { key: 'prologue.stamp', at: 0.6, kind: 'stamp' },
  { key: 'prologue.radio.1', at: 2.0, kind: 'radio' },
  { key: 'prologue.radio.2', at: 4.6, kind: 'radio' },
  { key: 'prologue.radio.3', at: 7.4, kind: 'cut' },
  { key: 'prologue.narration', at: 10.2, kind: 'narration' },
];

export class PrologueScene extends Phaser.Scene {
  private phase: Phase = 'call';
  private choice: PrologueChoice | null = null;
  /** Wie weit der Funkspruch schon gezeigt wurde. */
  private shown = 0;
  private timers: Phaser.Time.TimerEvent[] = [];

  constructor() {
    super('Prologue');
  }

  init(): void {
    this.phase = 'call';
    this.choice = null;
    this.shown = 0;
    this.timers = [];
    this.leaving = false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.abyss);
    audio.resume();
    audio.startDrone(DRONE_ROOT.site);
    audio.startSea();
    audio.setTension(0.75);

    this.redraw();
    watchResize(this, () => this.redraw());
    this.cameras.main.fadeIn(motionMs(900), 5, 8, 13);

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.toCreator();
      else if (this.phase === 'call' && (event.key === ' ' || event.key === 'Enter')) this.showWholeCall();
      else if (this.phase === 'choice' && event.key === '1') this.choose('rescue');
      else if (this.phase === 'choice' && event.key === '2') this.choose('wreck');
      else if (this.phase === 'outcome' && (event.key === ' ' || event.key === 'Enter')) this.toCreator();
    });
  }

  private redraw(): void {
    for (const timer of this.timers) timer.remove();
    this.timers = [];
    this.children.removeAll(true);

    const layout = layoutOf(this);
    addBackdrop(this, MENU_BACKDROP, this.phase === 'call' ? 0.12 : 0.2);

    // Ueberspringen — oben rechts, wie die Einstellungen anderswo.
    if (this.phase !== 'outcome') {
      const skip = this.add
        .text(layout.width - 16, 18, t('prologue.skip'), labelStyle(11, PALETTE.inkDim))
        .setOrigin(1, 0);
      makeTappable(skip);
      onTap(skip, {
        onTap: () => this.toCreator(),
        onHighlight: (on) => skip.setColor(on ? TEXT_COLOUR.coast : PALETTE.inkDim),
      });
    }

    if (this.phase === 'call') this.renderCall(layout.centreX, layout.columnWidth, layout.height);
    else if (this.phase === 'choice') this.renderChoice(layout.centreX, layout.width, layout.height, layout.compact);
    else this.renderOutcome(layout.centreX, layout.columnWidth, layout.height);
  }

  // --------------------------------------------------------------- Funkspruch

  private renderCall(centreX: number, width: number, height: number): void {
    const instant = getReducedMotion();
    const lines: Phaser.GameObjects.Text[] = [];
    let y = Math.max(60, height * 0.18);

    CALL.forEach((line, index) => {
      const style =
        line.kind === 'stamp'
          ? labelStyle(11, PALETTE.inkDim)
          : line.kind === 'narration'
            ? { ...titleStyle(20), color: PALETTE.ink }
            : { ...readoutStyle(14), color: line.kind === 'cut' ? TEXT_COLOUR.danger : TEXT_COLOUR.coast };
      const text = this.add
        .text(centreX, y, t(line.key, { souls: PROLOGUE_SOULS }), {
          ...style,
          align: 'center',
          wordWrap: { width },
          lineSpacing: 5,
        })
        .setOrigin(0.5, 0);
      y += text.height + (line.kind === 'stamp' ? 22 : 18);
      lines.push(text);

      const visible = instant || index < this.shown;
      text.setAlpha(visible ? 1 : 0);
      if (visible) return;

      this.timers.push(
        this.time.delayedCall(line.at * 1000, () => {
          this.shown = Math.max(this.shown, index + 1);
          this.tweens.add({ targets: text, alpha: 1, duration: 700 });
          this.cue(line.kind);
        }),
      );
    });

    // Weiter zur Wahl — erst, wenn der Funkspruch durch ist.
    const last = CALL[CALL.length - 1]?.at ?? 0;
    const proceed = (): void => {
      this.phase = 'choice';
      audio.impact(0.22);
      this.redraw();
    };
    if (instant || this.shown >= CALL.length) {
      createButton(this, centreX, height - 48, t('prologue.decide'), proceed, { width: 240 });
      return;
    }
    this.timers.push(
      this.time.delayedCall((last + 1.6) * 1000, () => {
        this.shown = CALL.length;
        createButton(this, centreX, height - 48, t('prologue.decide'), proceed, { width: 240 });
      }),
    );
    this.input.once('pointerdown', () => {
      if (this.phase === 'call' && this.shown < CALL.length) this.showWholeCall();
    });
  }

  /** Klang und Bewegung je Zeile: Ping, Alarm, abreissender Funk. */
  private cue(kind: (typeof CALL)[number]['kind']): void {
    if (kind === 'stamp') audio.cue('sonar');
    if (kind === 'radio') audio.cue('alert');
    if (kind === 'cut') {
      audio.cue('creak');
      if (!getReducedMotion()) this.cameras.main.shake(320, 0.004);
    }
    if (kind === 'narration') audio.impact(0.18);
  }

  private showWholeCall(): void {
    this.shown = CALL.length;
    this.redraw();
  }

  // -------------------------------------------------------------------- Wahl

  private renderChoice(centreX: number, width: number, height: number, compact: boolean): void {
    const prompt = this.add
      .text(centreX, 40, t('prologue.choice.prompt'), {
        ...titleStyle(compact ? 18 : 22),
        align: 'center',
        wordWrap: { width: Math.min(720, width - 32) },
      })
      .setOrigin(0.5, 0);
    const top = prompt.y + prompt.height + 24;

    // Zwei Karten: nebeneinander, wenn Platz ist, sonst untereinander.
    const gap = 16;
    const cardWidth = compact ? Math.min(420, width - 32) : Math.min(380, (width - 48 - gap) / 2);
    const available = height - top - 24;
    const cardHeight = compact ? Math.min(260, (available - gap) / 2) : Math.min(380, available);

    const options: { choice: PrologueChoice; plate: string; index: number }[] = [
      { choice: 'rescue', plate: TIER_PLATE.rescue, index: 0 },
      { choice: 'wreck', plate: TIER_PLATE.salvage, index: 1 },
    ];

    for (const option of options) {
      const x = compact ? centreX - cardWidth / 2 : centreX + (option.index === 0 ? -cardWidth - gap / 2 : gap / 2);
      const y = compact ? top + option.index * (cardHeight + gap) : top;
      this.card(x, y, cardWidth, cardHeight, option.choice, option.plate, option.index + 1);
    }
  }

  private card(
    x: number,
    y: number,
    width: number,
    height: number,
    choice: PrologueChoice,
    plate: string,
    number: number,
  ): void {
    const frame = this.add.rectangle(x, y, width, height, PALETTE.panel, 0.92).setOrigin(0, 0);
    frame.setStrokeStyle(1, PALETTE.panelEdge);

    const key = plateKey(plate);
    const imageHeight = Math.round(height * 0.5);
    if (this.textures.exists(key)) {
      const image = this.add.image(x + 1, y + 1, key).setOrigin(0, 0);
      const source = image.frame;
      const scale = Math.max((width - 2) / source.width, imageHeight / source.height);
      image.setScale(scale);
      image.setCrop(0, 0, (width - 2) / scale, imageHeight / scale);
      image.setAlpha(0.85);
    }

    const title = this.add.text(x + 16, y + imageHeight + 14, `${number}  ${t(`prologue.choice.${choice}`)}`, {
      fontFamily: FONT.display,
      fontSize: px(18),
      color: PALETTE.ink,
      wordWrap: { width: width - 32 },
    });
    this.add.text(x + 16, title.y + title.height + 8, t(`prologue.choice.${choice}.detail`, { souls: PROLOGUE_SOULS }), {
      ...readoutStyle(12),
      color: PALETTE.inkDim,
      wordWrap: { width: width - 32 },
      lineSpacing: 3,
    });

    // Die ganze Karte ist die Schaltflaeche.
    frame.setInteractive({ useHandCursor: true });
    onTap(frame, {
      onTap: () => this.choose(choice),
      onHighlight: (on) => frame.setStrokeStyle(on ? 1.5 : 1, on ? PALETTE.coast : PALETTE.panelEdge),
    });
    if (height < MIN_HIT) frame.setSize(width, MIN_HIT);
  }

  private choose(choice: PrologueChoice): void {
    if (this.phase !== 'choice') return;
    this.choice = choice;
    this.registry.set('prologueChoice', choice);
    this.phase = 'outcome';
    audio.cue(choice === 'rescue' ? 'confirm' : 'discovery');
    this.cameras.main.fadeOut(motionMs(400), 5, 8, 13);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.redraw();
      this.cameras.main.fadeIn(motionMs(800), 5, 8, 13);
    });
    if (getReducedMotion()) this.redraw();
  }

  // ------------------------------------------------------------------- Folge

  private renderOutcome(centreX: number, width: number, height: number): void {
    const choice = this.choice ?? 'rescue';
    let y = Math.max(60, height * 0.2);

    const outcome = this.add
      .text(centreX, y, t(`prologue.outcome.${choice}`, { souls: PROLOGUE_SOULS }), {
        ...readoutStyle(14),
        align: 'center',
        wordWrap: { width },
        lineSpacing: 5,
      })
      .setOrigin(0.5, 0);
    y += outcome.height + 20;

    const gain = this.add
      .text(centreX, y, t(`prologue.gain.${choice}`, { souls: PROLOGUE_SOULS }), {
        ...readoutStyle(12),
        color: choice === 'rescue' ? TEXT_COLOUR.base : TEXT_COLOUR.landmark,
        fontStyle: 'italic',
        align: 'center',
        wordWrap: { width },
      })
      .setOrigin(0.5, 0);
    y += gain.height + 36;

    const motto = this.add
      .text(centreX, y, t('prologue.motto'), {
        ...titleStyle(24),
        color: TEXT_COLOUR.coast,
        align: 'center',
        wordWrap: { width },
      })
      .setOrigin(0.5, 0)
      .setAlpha(getReducedMotion() ? 1 : 0);
    if (!getReducedMotion()) {
      this.timers.push(
        this.time.delayedCall(1600, () => {
          this.tweens.add({ targets: motto, alpha: 1, duration: 1200 });
          audio.impact(0.2);
        }),
      );
    }

    createButton(this, centreX, height - 48, t('prologue.continue'), () => this.toCreator(), { width: 240 });
  }

  private leaving = false;

  private toCreator(): void {
    if (this.leaving) return;
    this.leaving = true;
    for (const timer of this.timers) timer.remove();
    this.timers = [];
    this.cameras.main.fadeOut(motionMs(400), 5, 8, 13);
    const go = (): void => {
      this.scene.start('CharacterCreator');
    };
    if (getReducedMotion()) go();
    else this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, go);
  }
}
